import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

interface EnabledFeature {
  flag_key: string
  name: string
  description: string | null
  enabled_via: 'user_override' | 'user_disabled' | 'global_default'
}

// define all available flag keys here
export enum FeatureFlagKey {
  AddEntries = 'calendar.add_entries',
  AddWorkouts = 'calendar.add_workouts',
  AddAllDayEvents = 'calendar.add_all_day_events',
  GenerateWorkouts = 'calendar.generate_workouts',
  CalDavSharing = 'calendar.caldav',
  CalendarView = 'calendar',
  Testing = 'testing',
  Races = 'races',

  AthleteCalendar = 'athlete.calendar',
  AthleteTesting = 'athlete.testing',
  AthleteRaces = 'athlete.races',

  // Coach features
  CoachAnalysis = 'coach.analysis',
  CoachAutomations = 'coach.automations',

  // Workout Analysis features
  WBalChart = 'workout_analysis.wbal_chart',
}

export const useFeatureFlags = () => {
  const { user } = useAuth()
  const { data, isLoading, error, refetch } = useQuery<EnabledFeature[]>({
    queryKey: ['get_user_enabled_features'],
    enabled: user !== undefined,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_user_enabled_features' as any, {
        check_user_id: user?.id,
      })
      if (error) {
        throw error
      }
      return data as EnabledFeature[]
    },
  })

  const isFeatureEnabled = useCallback(
    (flagKey: FeatureFlagKey): boolean => {
      return data?.some((feature) => feature.flag_key === flagKey) ?? false
    },
    [data]
  )

  return {
    data,
    isLoading,
    error,
    isFeatureEnabled,
    refetch,
  }
}
